import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead from "@/components/base/SeoHead";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { getArticleContent, getArticleMeta } from "@/mocks/articleContents";

function getArticleJsonLd(articleMeta: { id: string; title: string; excerpt: string }, articleContent: any) {
  const siteUrl = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_SITE_URL) || "https://cssbuyspreadsheet.link";
  const articleUrl = `${siteUrl}/${articleMeta.id}`;

  return {
    "@type": "BlogPosting",
    headline: articleMeta.title,
    description: articleMeta.excerpt,
    url: articleUrl,
    datePublished: "2026-06-01T08:00:00+00:00",
    dateModified: "2026-06-17T08:00:00+00:00",
    inLanguage: "pt",
    isAccessibleForFree: true,
    author: {
      "@type": "Organization",
      name: "CSSBuy Spreadsheet",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "CSSBuy Spreadsheet",
      url: siteUrl,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": articleUrl,
    },
  };
}

function ArticleContentRenderer({ content }: { content: any }) {
  switch (content.type) {
    case "paragraph":
      return (
        <p
          className="text-foreground-700 text-base leading-relaxed mb-5"
          dangerouslySetInnerHTML={{ __html: content.data }}
        />
      );
    case "heading":
      return (
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
          {content.data}
        </h2>
      );
    case "table":
      return (
        <div className="overflow-x-auto mb-8 rounded-xl border border-background-200/70">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100">
                {content.data.headers.map((h: string, i: number) => (
                  <th key={i} className="text-left p-4 font-semibold text-foreground-950">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.data.rows.map((row: string[], ri: number) => (
                <tr
                  key={ri}
                  className={`border-t border-background-200/70 ${ri % 2 === 1 ? "bg-background-50" : ""}`}
                >
                  {row.map((cell: string, ci: number) => (
                    <td
                      key={ci}
                      className={`p-4 ${ci === 0 ? "font-medium text-foreground-800" : "text-foreground-600"}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "list":
      return (
        <ul className="space-y-3 mb-8">
          {content.data.map((item: any, i: number) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-sm"></i>
              </div>
              <p className="text-foreground-700 text-base leading-relaxed">
                <strong>{item.title}:</strong> {item.text}
              </p>
            </li>
          ))}
        </ul>
      );
    case "steps":
      return (
        <div className="space-y-4 mb-8">
          {content.data.map((step: any, i: number) => (
            <div
              key={i}
              className="flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold text-sm flex-shrink-0">
                {i + 1}
              </div>
              <div>
                <h4 className="font-semibold text-foreground-950 mb-1">{step.title}</h4>
                <p className="text-foreground-600 text-sm leading-relaxed">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      );
    case "faq":
      return (
        <div className="space-y-4 mb-8">
          {content.data.map((item: any, i: number) => (
            <div
              key={i}
              className="p-5 rounded-xl bg-background-50 border border-background-200/70"
            >
              <h4 className="font-semibold text-foreground-950 mb-2">{item.q}</h4>
              <p className="text-foreground-600 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      );
    case "cta":
      return (
        <div className="mt-10 p-6 md:p-8 rounded-xl bg-accent-500/10 border border-accent-200/50 text-center">
          <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">
            {content.data.title}
          </h3>
          <p className="text-foreground-600 text-sm mb-4 max-w-md mx-auto">
            {content.data.text}
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm cursor-pointer hover:bg-accent-600 transition-all whitespace-nowrap"
          >
            Acessar CSSBuy Spreadsheet
            <i className="ri-arrow-right-up-line text-base"></i>
          </a>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleContent = slug ? getArticleContent(slug) : undefined;
  const articleMeta = slug ? getArticleMeta(slug) : undefined;

  if (!articleContent || !articleMeta) {
    return (
      <>
        <SeoHead
          title="Artigo não encontrado - CSSBuy Spreadsheet"
          description="O artigo que você procura não está disponível. Volte ao início e explore nossos guias sobre CSSBuy Spreadsheet."
          canonicalPath={`/${slug || ""}`}
          noindex={true}
        />
        <Navbar />
        <main className="pt-24 pb-16 px-4 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">
            Artigo não encontrado
          </h1>
          <p className="text-foreground-500 mb-6">
            O artigo que você procura não está disponível.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm cursor-pointer hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar ao Início
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = getArticleJsonLd(articleMeta, articleContent);
  const relatedArticles = articles.filter((a) => a.id !== slug).slice(0, 4);

  return (
    <>
      <SeoHead
        title={`${articleContent.title} | CSSBuy Spreadsheet`}
        description={articleContent.excerpt}
        keywords={`cssbuy spreadsheet, cssbuy, ${articleContent.title.toLowerCase().replace(/\s+/g, ", ")}`}
        canonicalPath={`/${articleMeta.id}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <main>
        {/* Mini Hero */}
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center">
            <nav className="flex items-center justify-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-accent-600 transition-colors">
                Início
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-400">{articleContent.title}</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4 max-w-3xl mx-auto leading-tight">
              {articleContent.title}
            </h1>
            <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto">
              {articleContent.excerpt}
            </p>
            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-foreground-400">
              <span>
                <i className="ri-calendar-line mr-1"></i>Atualizado: Junho 2026
              </span>
              <span>
                <i className="ri-time-line mr-1"></i>Leitura: 7 min
              </span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-[1200px] mx-auto flex flex-col lg:flex-row gap-10 lg:gap-12">
            {/* Left - Main Content */}
            <article className="lg:w-[70%]">
              <div className="prose-custom">
                {articleContent.content.map((block, index) => (
                  <ArticleContentRenderer key={index} content={block} />
                ))}
              </div>
            </article>

            {/* Right - Sidebar */}
            <aside className="lg:w-[30%]">
              <div className="lg:sticky lg:top-24 space-y-6">
                {/* Main CTA */}
                <div className="p-5 rounded-xl bg-primary-500 text-center">
                  <p className="text-background-50/90 text-sm mb-3">
                    Quer a planilha completa?
                  </p>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-50 text-primary-600 font-semibold text-sm cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    Ver Spreadsheet Principal
                    <i className="ri-arrow-right-up-line text-base"></i>
                  </a>
                </div>

                {/* Categories sidebar */}
                <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <h4 className="font-heading font-semibold text-foreground-950 mb-3 text-sm">
                    Categorias da Planilha
                  </h4>
                  <ul className="space-y-2">
                    {categories.map((cat) => (
                      <li key={cat.id}>
                        <a
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center justify-between text-foreground-600 hover:text-accent-600 text-sm transition-colors py-1"
                        >
                          {cat.name}
                          <i className="ri-arrow-right-up-line text-xs"></i>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Related articles */}
                <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <h4 className="font-heading font-semibold text-foreground-950 mb-3 text-sm">
                    Artigos Relacionados
                  </h4>
                  <ul className="space-y-3">
                    {relatedArticles.map((article) => (
                      <li key={article.id}>
                        <Link to={`/${article.id}`} className="block group">
                          <h5 className="text-foreground-700 group-hover:text-accent-600 text-sm font-medium transition-colors leading-snug">
                            {article.title}
                          </h5>
                          <p className="text-foreground-400 text-xs mt-1 line-clamp-1">
                            {article.excerpt}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}